import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    type Dispatch,
    type MutableRefObject,
    type ReactNode,
    type SetStateAction,
} from "react";
import type { PickerItemRegistration } from "./PickerContext";
import type {
    PickerCellEditor,
    PickerFlyoutAutoOpen,
    PickerFlyoutOptions,
    PickerFlyoutQueryScope,
    PickerItemValue,
    PickerRootKind,
} from "./Picker.types";

/**
 * What every level of a cascading picker shares with the top root: the
 * selection it commits into, how to close the whole chain, and the id that
 * marks every popup of the chain so ancestors can tell a click in a
 * descendant level from a real outside press.
 */
export interface PickerChainContextValue {
    chainId: string;
    kind: PickerRootKind;
    isMultiple: boolean;
    selectedValues: readonly PickerItemValue[];
    /** Commits leaf values at the top root (single: the leaf; multiple: the toggled set). */
    commitValues: (values: readonly PickerItemValue[]) => void;
    /** Lets the top root label leaves rendered deeper down. */
    registerItem: (registration: PickerItemRegistration) => void;
    closeAll: () => void;
    /** Portal target of the root's popup and every level; a `Picker.Popup` `container` wins. */
    container?: HTMLElement;
    /** Level options every `Picker.Flyout` in the chain starts from. */
    flyoutDefaults?: PickerFlyoutOptions;
    flyoutQueryScope: PickerFlyoutQueryScope;
    flyoutAutoOpen: PickerFlyoutAutoOpen;
    /** The root's `autoHighlight`, which searchable levels follow. */
    isAutoHighlight: boolean;
    /** The root's `cellEditor`: grid keys on every search and text input of the chain. */
    cellEditor?: PickerCellEditor;
}

/** A level's options: the chain defaults, then the level's own set fields. */
export const mergeFlyoutOptions = (
    defaults: PickerFlyoutOptions | undefined,
    overrides: PickerFlyoutOptions,
): PickerFlyoutOptions => {
    const merged: PickerFlyoutOptions = { ...defaults };
    for (const [key, value] of Object.entries(overrides)) {
        if (value !== undefined) {
            Object.assign(merged, { [key]: value });
        }
    }
    return merged;
};

/** Imperative surface a flyout row exposes to its level for keyboard opening. */
export interface PickerFlyoutHandle {
    open: () => void;
}

/**
 * Per-level state: which flyout row is open (one at a time), the registry
 * keyboard navigation uses to open the highlighted row, and how to close the
 * level itself when it is a flyout.
 */
export interface PickerLevelContextValue {
    depth: number;
    openFlyoutId: string | null;
    setOpenFlyoutId: Dispatch<SetStateAction<string | null>>;
    flyouts: MutableRefObject<Map<string, PickerFlyoutHandle>>;
    /** The level's popup element, where the highlighted row is looked up. */
    popupRef: MutableRefObject<HTMLElement | null>;
    /** Records the level's popup element in `popupRef`; the popup's ref callback. */
    registerPopup: (element: HTMLElement | null) => void;
    /** Closes this flyout level and returns focus to its row; absent at the top. */
    closeLevel?: () => void;
}

/** What `Picker.Item` reads to behave as the row of a `Picker.Flyout`. */
export interface PickerFlyoutRowContextValue {
    id: string;
    isOpen: boolean;
    open: () => void;
    setAnchor: (element: HTMLElement | null) => void;
    onPointerEnter: () => void;
    onPointerLeave: () => void;
}

export const PickerChainContext = createContext<PickerChainContextValue | null>(
    null,
);

export const PickerFlyoutRowContext =
    createContext<PickerFlyoutRowContextValue | null>(null);

export const usePickerFlyoutRow = () => useContext(PickerFlyoutRowContext);

export const PickerLevelContext = createContext<PickerLevelContextValue | null>(
    null,
);

export const usePickerChain = (): PickerChainContextValue | null =>
    useContext(PickerChainContext);

export const usePickerLevel = (): PickerLevelContextValue | null =>
    useContext(PickerLevelContext);

/** The chain of the enclosing list root; throws for a flyout part rendered outside one. */
export const usePickerChainOrThrow = (): PickerChainContextValue => {
    const chain = useContext(PickerChainContext);
    if (chain === null) {
        throw new Error("Picker.Flyout must be rendered inside a Picker root");
    }
    return chain;
};

/** The enclosing level; throws for a flyout part rendered outside a list root. */
export const usePickerLevelOrThrow = (): PickerLevelContextValue => {
    const level = useContext(PickerLevelContext);
    if (level === null) {
        throw new Error("Picker.Flyout must be rendered inside a Picker root");
    }
    return level;
};

/**
 * Provides the per-level flyout state; every root and every flyout level
 * renders one. The open-flyout pair is owned by the level's list root, which
 * drops its open flyout in the same commit that closes its own popup, so a
 * level never outlives the row it is anchored to.
 */
export const PickerLevelProvider = ({
    depth,
    openFlyoutId,
    setOpenFlyoutId,
    closeLevel,
    children,
}: {
    depth: number;
    openFlyoutId: string | null;
    setOpenFlyoutId: Dispatch<SetStateAction<string | null>>;
    closeLevel?: () => void;
    children: ReactNode;
}) => {
    const flyouts = useRef(new Map<string, PickerFlyoutHandle>());
    const popupRef = useRef<HTMLElement | null>(null);
    const registerPopup = useCallback((element: HTMLElement | null) => {
        popupRef.current = element;
    }, []);
    const value = useMemo<PickerLevelContextValue>(
        () => ({
            depth,
            openFlyoutId,
            setOpenFlyoutId,
            flyouts,
            popupRef,
            registerPopup,
            closeLevel,
        }),
        [depth, openFlyoutId, setOpenFlyoutId, registerPopup, closeLevel],
    );

    return (
        <PickerLevelContext.Provider value={value}>
            {children}
        </PickerLevelContext.Provider>
    );
};

const guardedReasons = new Set(["outside-press", "focus-out", "escape-key"]);

const levelDepthOf = (target: EventTarget | null, chainId: string) => {
    if (!(target instanceof Element)) {
        return null;
    }
    const popup = target.closest(`[data-picker-chain="${chainId}"]`);
    const depth = popup?.getAttribute("data-picker-level");
    return depth === null || depth === undefined ? null : Number(depth);
};

/**
 * Whether a Base UI close request belongs to a descendant level of the chain
 * (a press, focus move or Escape inside a deeper popup), in which case the
 * ancestor must stay open and cancel the request.
 */
export const isCloseFromDescendantLevel = ({
    reason,
    event,
    chainId,
    depth,
}: {
    reason: string;
    event: Event;
    chainId: string;
    depth: number;
}): boolean => {
    if (!guardedReasons.has(reason)) {
        return false;
    }
    const targets = [event.target];
    if (event instanceof FocusEvent) {
        targets.push(event.relatedTarget);
    }
    return targets.some((target) => {
        const targetDepth = levelDepthOf(target, chainId);
        return targetDepth !== null && targetDepth > depth;
    });
};
